import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function ArbitrageAdvert() {
  const [profit, setProfit] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  // Simulate real-time profit counter
  useEffect(() => {
    const interval = setInterval(() => {
      setProfit(prev => {
        const newProfit = prev + Math.random() * 50 + 20;
        return newProfit > 10000 ? 0 : newProfit;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Pulse animation for visibility
  useEffect(() => {
    const pulseInterval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => setIsVisible(true), 100);
    }, 3000);

    return () => clearInterval(pulseInterval);
  }, []);

  return (
    <section className="relative py-16 sm:py-20 overflow-hidden bg-gradient-to-br from-emerald-900 via-teal-900 to-cyan-900">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating Money Icons */}
        <div className="absolute top-10 left-10 animate-bounce">
          <i className="ri-money-dollar-circle-line text-6xl text-emerald-400/20"></i>
        </div>
        <div className="absolute top-20 right-20 animate-pulse delay-500">
          <i className="ri-exchange-dollar-line text-5xl text-teal-400/20"></i>
        </div>
        <div className="absolute bottom-20 left-1/4 animate-bounce delay-1000">
          <i className="ri-funds-line text-7xl text-cyan-400/20"></i>
        </div>
        <div className="absolute bottom-10 right-1/3 animate-pulse delay-700">
          <i className="ri-line-chart-line text-6xl text-green-400/20"></i>
        </div>

        {/* Animated Circles */}
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Arbitrage Banner */}
        <div className="relative bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-3xl shadow-2xl overflow-hidden border-4 border-emerald-400/30">
          
          {/* Glowing Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 via-transparent to-cyan-400/20 animate-pulse"></div>
          
          {/* Content */}
          <div className="relative z-10 p-8 sm:p-12 lg:p-16">
            
            {/* Top Badge */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 bg-yellow-400 text-black px-6 py-2 rounded-full font-bold text-sm shadow-lg animate-bounce">
                <i className="ri-flashlight-fill text-xl"></i>
                <span>EXCLUSIVE OPPORTUNITY</span>
                <i className="ri-flashlight-fill text-xl"></i>
              </div>
            </div>

            {/* Main Headline */}
            <div className="text-center mb-8">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
                <span className="block">💰 Arbitrage Trading</span>
                <span className="block bg-gradient-to-r from-yellow-300 via-orange-300 to-yellow-300 bg-clip-text text-transparent">
                  Earn Profits Automatically!
                </span>
              </h2>
              <p className="text-xl sm:text-2xl text-emerald-100 font-semibold max-w-3xl mx-auto">
                Make money from price differences across exchanges - 100% automated, zero risk!
              </p>
            </div>

            {/* Live Profit Counter */}
            <div className="bg-black/30 backdrop-blur-sm rounded-2xl p-6 sm:p-8 mb-8 border-2 border-yellow-400/50 shadow-xl">
              <div className="text-center">
                <div className="text-sm sm:text-base text-emerald-200 mb-2 font-medium">
                  🔥 Live Arbitrage Profits (Last Hour)
                </div>
                <div className={`text-5xl sm:text-6xl lg:text-7xl font-black bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent transition-all duration-500 ${isVisible ? 'opacity-100 scale-100' : 'opacity-50 scale-95'}`}>
                  ${profit.toFixed(2)}
                </div>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-green-300 font-semibold text-sm">Real-time updates</span>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-robot-2-line text-white text-2xl"></i>
                </div>
                <h3 className="font-bold text-white text-center mb-2">AI-Powered</h3>
                <p className="text-emerald-100 text-sm text-center">Smart algorithms find the best opportunities</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-shield-check-line text-white text-2xl"></i>
                </div>
                <h3 className="font-bold text-white text-center mb-2">Zero Risk</h3>
                <p className="text-emerald-100 text-sm text-center">Guaranteed profits with no market exposure</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-time-line text-white text-2xl"></i>
                </div>
                <h3 className="font-bold text-white text-center mb-2">24/7 Trading</h3>
                <p className="text-emerald-100 text-sm text-center">Automated trading never sleeps</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center mb-3 mx-auto">
                  <i className="ri-rocket-line text-white text-2xl"></i>
                </div>
                <h3 className="font-bold text-white text-center mb-2">Instant Setup</h3>
                <p className="text-emerald-100 text-sm text-center">Start earning in under 2 minutes</p>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4 mb-8 bg-black/20 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-black text-yellow-400">15-25%</div>
                <div className="text-emerald-200 text-sm font-medium mt-1">Daily Returns</div>
              </div>
              <div className="text-center border-l border-r border-white/20">
                <div className="text-3xl sm:text-4xl font-black text-green-400">$50K+</div>
                <div className="text-emerald-200 text-sm font-medium mt-1">Avg. Monthly Profit</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-black text-cyan-400">10,000+</div>
                <div className="text-emerald-200 text-sm font-medium mt-1">Active Traders</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link 
                to="/arbitrage"
                className="group relative bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white px-10 py-5 rounded-2xl font-black text-xl hover:from-yellow-500 hover:to-red-600 transform hover:scale-110 transition-all duration-300 shadow-2xl hover:shadow-yellow-500/50 whitespace-nowrap cursor-pointer overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-3">
                  <i className="ri-money-dollar-circle-line text-2xl"></i>
                  Start Arbitrage Now
                  <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform"></i>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              </Link>

              <Link 
                to="/calculator"
                className="bg-white/20 backdrop-blur-sm text-white px-8 py-5 rounded-2xl font-bold text-lg border-2 border-white/40 hover:bg-white/30 transform hover:scale-105 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-calculator-line text-xl"></i>
                Calculate Profits
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-emerald-100">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-fill text-green-400 text-xl"></i>
                <span className="text-sm font-medium">Verified Platform</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-lock-fill text-yellow-400 text-xl"></i>
                <span className="text-sm font-medium">Secure Transactions</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-customer-service-2-fill text-cyan-400 text-xl"></i>
                <span className="text-sm font-medium">24/7 Support</span>
              </div>
            </div>
          </div>

          {/* Decorative Corner Elements */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-yellow-400/30 to-transparent rounded-br-full"></div>
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-cyan-400/30 to-transparent rounded-tl-full"></div>
        </div>

        {/* Bottom Testimonial */}
        <div className="mt-8 text-center">
          <div className="inline-block bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-4 border border-white/20">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                ))}
              </div>
              <span className="text-white font-bold">5.0/5.0</span>
            </div>
            <p className="text-emerald-100 italic text-sm max-w-2xl">
              "I've been using PepCoin's arbitrage feature for 3 months and made over $45,000 in pure profit. It's completely automated and works like magic!" - Sarah M., Professional Trader
            </p>
          </div>
        </div>
      </div>

      {/* Floating Action Button (Sticky) */}
      <div className="fixed bottom-8 right-8 z-50 animate-bounce">
        <Link 
          to="/arbitrage"
          className="group bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-6 py-4 rounded-full font-black text-sm shadow-2xl hover:shadow-yellow-500/50 transform hover:scale-110 transition-all duration-300 cursor-pointer flex items-center gap-2"
        >
          <i className="ri-money-dollar-circle-fill text-xl"></i>
          <span className="hidden sm:inline">Quick Arbitrage</span>
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
            🔥
          </div>
        </Link>
      </div>
    </section>
  );
}
